import { orderBy } from 'lodash';
import { EventType } from '../../Chat.enum';
import { tChatEvent } from '../../Chat.types';
import {
  isInteractiveOutbound,
  isInteractiveReply,
} from '../MessageBar/components/InteractiveAction/InteractiveAction.functions';
import { localDayKey } from './bubbleDate/bubbleDate.functions';

export type tBubbleLayoutEntry = {
  /** First visible item of its local day: a day separator goes before it. */
  startsDay: boolean;
  /** Local calendar day, "YYYY-MM-DD". */
  dayKey: string;
  /** First bubble of a run of plain messages from the same sender. */
  groupStart: boolean;
};

export type tBubbleLayoutOptions = {
  /** Items the thread does not render (a scheduled card that gave way, album photos folded into the album). */
  isHidden: (event: tChatEvent) => boolean;
  /** Album anchors render their own bubble and never join a run. */
  isAlbumAnchor: (event: tChatEvent) => boolean;
};

/**
 * Who a plain message bubble belongs to, or null for anything that breaks a
 * run (notes, templates, system lines, interactive messages, albums). Messages
 * sent from the phone carry an empty `createdBy`, so they group together.
 */
function runKeyOf(event: tChatEvent, options: tBubbleLayoutOptions) {
  if (options.isAlbumAnchor(event)) return null;
  // BUG-1063: a tombstone belongs to the run of whoever was talking, so it
  // does not split a lead's messages in three and hand each piece a new tail.
  if (
    event.type !== EventType.MESSAGE &&
    event.type !== EventType.OUTBOUND_DELETED &&
    event.type !== EventType.INBOUND_DELETED
  ) {
    return null;
  }
  const interactive = event.content?.interactive;
  if (isInteractiveReply(interactive) || isInteractiveOutbound(interactive)) return null;
  const fromMe = Boolean(event.message?.fromMe);
  const sender = fromMe ? (event.createdBy ?? '') : (event.message?.participant?.handle ?? '');
  return `${fromMe ? 'out' : 'in'}|${sender}`;
}

/**
 * The order the thread renders in: by time, with arrival order kept for
 * events of the same instant (the sort is stable). Older history is merged
 * sorted, but the socket appends a new event at the end, so a late one can sit
 * after newer messages; rendering and the layout below both read this list so
 * a day or a run never restarts because of arrival order.
 */
export function orderThreadEvents(events: tChatEvent[]) {
  return orderBy(
    events,
    [
      (event) => {
        const time = Date.parse(event.createdAt);
        return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
      },
    ],
    ['asc'],
  );
}

/**
 * Walks the thread once, over the items it renders (in the order of
 * orderThreadEvents), and says where a day starts and where a run of bubbles
 * starts. Runs never cross a day.
 */
export function buildBubbleLayout(events: tChatEvent[], options: tBubbleLayoutOptions) {
  const layout = new Map<string, tBubbleLayoutEntry>();
  let previousDay: string | null = null;
  let previousRun: string | null = null;

  for (const event of events) {
    if (options.isHidden(event)) continue;
    const dayKey = localDayKey(event.createdAt);
    const startsDay = dayKey !== previousDay;
    const run = runKeyOf(event, options);
    layout.set(event.id, {
      startsDay,
      dayKey,
      groupStart: startsDay || run === null || run !== previousRun,
    });
    previousDay = dayKey;
    previousRun = run;
  }

  return layout;
}
